/**
 * Read-side tracking_items → legacy log fields (/logs table, scope tree, search).
 * Keep in lockstep with erp_api/routes/controllers/logTracking.ts.
 */


import type { ActivityGroup, ActivityGroupKind, ActivityGroupLink } from './activityGroupsModel';
import { parseRecordLogLabelParts } from 'pages/Records/recordLogLabel';

/** Loose tracking row shape — keep in lockstep with erp_api/routes/controllers/logTracking.ts */
export type TrackingItemLike = {
  trackingItem?: string,
  fieldKey?: string,
  recordKind?: string,
  recordCode?: string,
  value?: string,
};

/** /logs Groups — only these head scopes, in this parent order. */
export const LOGS_HEAD_SCOPE_KINDS = [
  'organization',
  'location',
  'department',
  'management',
] as const;

export type LogsHeadScopeKind = typeof LOGS_HEAD_SCOPE_KINDS[number];

const isLogsHeadScopeKind = (kind: string): kind is LogsHeadScopeKind => (
  (LOGS_HEAD_SCOPE_KINDS as readonly string[]).includes(kind)
);

/** Walk designation/employee to reach department for Management. */
const HEAD_SCOPE_WALK_PARENT: Record<string, string | null> = {
  organization: null,
  location: 'organization',
  department: 'location',
  designation: 'department',
  employee: 'designation',
  management: 'employee',
  team: 'department',
};

export type ParsedHeadScope = {
  kind: string,
  code: string,
  name: string,
  parentKind?: string,
  /** Parent name or code from the tracker — match either, never show this as a label. */
  parentRef?: string,
};

export type ResolvedLogTracking = {
  hasTrackers: boolean,
  headScopes: ParsedHeadScope[],
  /** Deepest department-style scope name for branch filters. */
  departmentName?: string,
  objectName?: string,
  scopeName?: string,
  skuCode?: string,
  skuName?: string,
  glName?: string,
  glCode?: string,
  costPrice?: string,
  sellingPrice?: string,
  uom?: string,
  email?: string,
  /** External compute marker (track_compute). */
  computeValue?: string,
};

/** Transaction JSON on track_transaction — keep in lockstep with masterDataTracking.ts */
export type AssetRentingTransactionTrack = {
  price: string,
  customerId?: string,
  customerName?: string,
  customerEmail?: string,
  customerNumber?: string,
  uom?: string,
  assetCode?: string,
  transactionType?: string,
};

export const parseTrackTransactionValue = (raw: unknown): AssetRentingTransactionTrack | null => {
  let src: unknown = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      src = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!src || typeof src !== 'object' || Array.isArray(src)) return null;
  const row = src as Record<string, unknown>;
  const price = String(row.price ?? '').trim().slice(0, 24);
  if (!price) return null;
  const clipOpt = (value: unknown, max: number): string | undefined => {
    const out = String(value ?? '').trim().slice(0, max);
    return out || undefined;
  };
  const out: AssetRentingTransactionTrack = { price };
  const customerId = clipOpt(row.customerId, 80);
  if (customerId) out.customerId = customerId;
  const customerName = clipOpt(row.customerName, 120);
  if (customerName) out.customerName = customerName;
  const customerEmail = clipOpt(row.customerEmail, 120);
  if (customerEmail) out.customerEmail = customerEmail;
  const customerNumber = clipOpt(row.customerNumber, 40);
  if (customerNumber) out.customerNumber = customerNumber;
  const uom = clipOpt(row.uom, 40);
  if (uom) out.uom = uom;
  const assetCode = clipOpt(row.assetCode, 40);
  if (assetCode) out.assetCode = assetCode;
  const transactionType = clipOpt(row.transactionType, 40);
  if (transactionType) out.transactionType = transactionType;
  return out;
};

const trackingItemsOn = (
  log: { tracking_items?: TrackingItemLike[] | null, trackingItems?: TrackingItemLike[] | null },
): TrackingItemLike[] => {
  const raw = log.tracking_items ?? log.trackingItems;
  return Array.isArray(raw) ? raw : [];
};

/** `Engineering|parent:location:HQ` → name + parent. */
export const parseHeadScopeValue = (
  value: string,
): { name: string, parentKind?: string, parentRef?: string } => {
  const raw = String(value || '').trim();
  const parentIdx = raw.indexOf('|parent:');
  if (parentIdx === -1) return { name: raw };
  const name = raw.slice(0, parentIdx).trim();
  const parentPart = raw.slice(parentIdx + '|parent:'.length);
  const colon = parentPart.indexOf(':');
  if (colon === -1) return { name, parentKind: parentPart.trim() };
  return {
    name,
    parentKind: parentPart.slice(0, colon).trim(),
    parentRef: parentPart.slice(colon + 1).trim(),
  };
};

const firstItem = (
  items: TrackingItemLike[],
  trackingItem: string,
): TrackingItemLike | undefined => (
  items.find((row) => String(row?.trackingItem || '') === trackingItem)
);

const departmentKind = (kind: string): boolean => (
  kind === 'department' || kind === 'team'
);

/** Custom type codes must never appear in the /logs Value column. */
export const isCustomMasterDataTypeCodeValue = (value: unknown): boolean => (
  /^DATA-\d+$/i.test(String(value ?? '').trim())
);

const relatedGroupsOnLog = (
  log: { relatedGroups?: unknown, related_groups?: unknown },
): unknown[] => {
  const raw = log.relatedGroups ?? log.related_groups;
  return Array.isArray(raw) ? raw : [];
};

const RECORD_SAVE_EVENT_TYPES = new Set([
  'Record added',
  'Record change',
  'Record updated',
  'Record removed',
]);

const logEventTypeRaw = (log: {
  eventType?: string | null,
  event_type?: string | null,
}): string => String(
  log.eventType || (log as { event_type?: string | null }).event_type || '',
).trim();

/** Parse activity_logs.value — skips N/A, DATA-#### codes, and non-numeric text. */
export const parseLogMoneyValue = (raw: unknown): number | null => {
  if (raw == null || raw === '' || raw === 'N/A') return null;
  if (isCustomMasterDataTypeCodeValue(raw)) return null;
  const num = Number(String(raw).replace(/,/g, '').trim());
  return Number.isFinite(num) ? num : null;
};

/** True when value holds a non-zero money amount and this is not a master-data record-save row. */
export const logHasMoneyValue = (
  log: {
    value?: string | number | null,
    eventType?: string | null,
    event_type?: string | null,
  },
): boolean => {
  if (RECORD_SAVE_EVENT_TYPES.has(logEventTypeRaw(log))) return false;
  const amount = parseLogMoneyValue(log.value);
  return amount != null && amount !== 0;
};

/** /logs Value column — activity_logs.value only. */
export const logMoneyValueForDisplay = (
  log: {
    value?: string | number | null,
    eventType?: string | null,
    event_type?: string | null,
  },
): string | null => {
  if (RECORD_SAVE_EVENT_TYPES.has(logEventTypeRaw(log))) return null;
  const amount = parseLogMoneyValue(log.value);
  if (amount == null || amount === 0) return null;
  return String(log.value).trim();
};

export const logHasTransactionTracker = logHasMoneyValue;

/** @deprecated Alias — reads activity_logs.value. */
export const logValueFromTransactionTracker = logMoneyValueForDisplay;

/** Activity group / head scope — legacy helper for scope tree wiring. */
export const logHasAttachedGroupForValue = (
  log: {
    relatedGroups?: unknown
    related_groups?: unknown
    tracking_items?: TrackingItemLike[] | null
    trackingItems?: TrackingItemLike[] | null
  },
  resolved: Pick<ResolvedLogTracking, 'headScopes'>,
): boolean => {
  if (resolved.headScopes.length > 0 || relatedGroupsOnLog(log).length > 0) return true;
  return trackingItemsOn(log).some((item) => (
    item.trackingItem === 'track_asset_mobility'
  ));
};

export const resolveLogTracking = (
  log: { tracking_items?: TrackingItemLike[] | null, trackingItems?: TrackingItemLike[] | null },
): ResolvedLogTracking => {
  const items = trackingItemsOn(log);
  const out: ResolvedLogTracking = { hasTrackers: items.length > 0, headScopes: [] };
  if (!items.length) return out;

  items.forEach((item) => {
    if (item.trackingItem !== 'track_head_scope') return;
    const kind = String(item.recordKind || '').trim().toLowerCase();
    let code = String(item.recordCode || '').trim();
    const parsed = parseHeadScopeValue(String(item.value || ''));
    let name = parsed.name;
    // Structural scopes: Nigeria(LOC-001) → Nigeria so the tree merges with erp attach.
    if (isLogsHeadScopeKind(kind)) {
      const parts = parseRecordLogLabelParts(name);
      if (parts.name) name = parts.name;
      if (!code && parts.code) code = parts.code;
      if (!name) return;
      const parentParts = parseRecordLogLabelParts(parsed.parentRef || '');
      out.headScopes.push({
        kind,
        code,
        name,
        parentKind: parsed.parentKind,
        parentRef: parentParts.name || parsed.parentRef,
      });
      return;
    }
    if (!kind || (!code && !name)) return;
    out.headScopes.push({
      kind,
      code,
      name,
      parentKind: parsed.parentKind,
      parentRef: parsed.parentRef,
    });
  });

  const deptScope = [...out.headScopes].reverse().find((s) => departmentKind(s.kind));
  if (deptScope) {
    out.departmentName = deptScope.name;
  } else { out.departmentName = 'Ungrouped' };

  const objectItem = firstItem(items, 'track_object');
  if (objectItem) out.objectName = String(objectItem.value || '').trim();

  const scopeItem = firstItem(items, 'track_scope');
  if (scopeItem) out.scopeName = String(scopeItem.value || '').trim();

  const emailItem = firstItem(items, 'track_email');
  if (emailItem) out.email = String(emailItem.value || '').trim();

  const skuItem = firstItem(items, 'track_sku');
  if (skuItem) {
    out.skuCode = String(skuItem.recordCode || '').trim();
    out.skuName = String(skuItem.value || '').trim();
  }

  const glItem = firstItem(items, 'track_gl');
  if (glItem) {
    out.glCode = String(glItem.recordCode || '').trim();
    out.glName = String(glItem.value || '').trim();
  }

  const costItem = firstItem(items, 'track_cost_price');
  if (costItem) out.costPrice = String(costItem.value || '').trim();

  const sellItem = firstItem(items, 'track_selling_price');
  if (sellItem) out.sellingPrice = String(sellItem.value || '').trim();

  const uomItem = firstItem(items, 'track_uom');
  if (uomItem) out.uom = String(uomItem.value || '').trim();

  const computeItem = firstItem(items, 'track_compute');
  if (computeItem) out.computeValue = String(computeItem.value || '').trim();

  return out;
};

/** Stable scope id from display name — never use record codes in the tree. */
export const trackerScopeId = (kind: string, name: string): string => {
  const k = String(kind || '').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_');
  const n = String(name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 48);
  return `trk_${k}_${n || 'unnamed'}`;
};

const normalizeTrackerScopeKind = (kind: string): ActivityGroupKind => {
  const k = String(kind || '').trim().toLowerCase();
  if (k === 'organization') return 'organization';
  if (k === 'location') return 'property';
  if (k === 'department' || k === 'team') return 'department';
  return 'custom';
};

const normKey = (value: string): string => String(value || '').trim().toLowerCase();

type HeadScopeIndex = {
  byKindName: Map<string, ParsedHeadScope>,
  byKindCode: Map<string, ParsedHeadScope>,
};

const collectHeadScopeIndex = (
  logs: Array<{ tracking_items?: TrackingItemLike[] | null, trackingItems?: TrackingItemLike[] | null }> | null | undefined,
): HeadScopeIndex => {
  const byKindName = new Map<string, ParsedHeadScope>();
  const byKindCode = new Map<string, ParsedHeadScope>();
  (logs || []).forEach((log) => {
    resolveLogTracking(log).headScopes.forEach((scope) => {
      if (!scope.kind) return;
      if (scope.name) byKindName.set(`${scope.kind}:${normKey(scope.name)}`, scope);
      if (scope.code) byKindCode.set(`${scope.kind}:${normKey(scope.code)}`, scope);
    });
  });
  return { byKindName, byKindCode };
};

const findHeadScope = (
  index: HeadScopeIndex,
  kind: string,
  ref: string,
): ParsedHeadScope | undefined => {
  const key = normKey(ref);
  if (!kind || !key) return undefined;
  return index.byKindName.get(`${kind}:${key}`) || index.byKindCode.get(`${kind}:${key}`);
};

/** Walk parent pointers until `targetKind` (skip designation / employee). */
const walkToHeadKind = (
  index: HeadScopeIndex,
  startKind: string | undefined,
  startRef: string | undefined,
  targetKind: string,
): ParsedHeadScope | undefined => {
  let kind = String(startKind || '').trim().toLowerCase();
  let ref = String(startRef || '').trim();
  const seen = new Set<string>();
  for (let i = 0; i < 8; i += 1) {
    if (!kind || !ref) return undefined;
    const token = `${kind}:${normKey(ref)}`;
    if (seen.has(token)) return undefined;
    seen.add(token);
    const row = findHeadScope(index, kind, ref);
    if (!row) {
      kind = HEAD_SCOPE_WALK_PARENT[kind] || '';
      continue;
    }
    if (row.kind === targetKind) return row;
    kind = String(row.parentKind || HEAD_SCOPE_WALK_PARENT[row.kind] || '').trim().toLowerCase();
    ref = String(row.parentRef || '').trim();
  }
  return undefined;
};

/** Merge head scopes from log trackers into the activity groups DAG. */
export const augmentActivityGroupsFromLogTrackers = (
  groupsIn: ActivityGroup[],
  linksIn: ActivityGroupLink[],
  logs: Array<{ tracking_items?: TrackingItemLike[] | null, trackingItems?: TrackingItemLike[] | null }> | null | undefined,
): { groups: ActivityGroup[], links: ActivityGroupLink[] } => {
  const groupById = new Map<string, ActivityGroup>();
  // Clone rows — Redux/Immer groups are frozen; ensureVisible must not mutate them in place.
  const groups: ActivityGroup[] = (groupsIn || []).map((g) => ({
    ...g,
    groupTypes: Array.isArray(g.groupTypes) ? [...g.groupTypes] : [],
  }));
  groups.forEach((g) => groupById.set(g.id, g));

  const linkKey = (parentId: string, childId: string) => `${parentId}\0${childId}`;
  const linkSet = new Set((linksIn || []).map((l) => linkKey(l.parentId, l.childId)));
  const links = [...(linksIn || [])];

  const orgScope = groups.find((g) => g.kind === 'organization');

  const ensureVisible = (kind: LogsHeadScopeKind, name: string): string | null => {
    const label = String(name || '').trim();
    if (!label) return null;
    if (kind === 'organization') return orgScope?.id || null;
    const id = trackerScopeId(kind, label);
    const existing = groupById.get(id);
    if (existing) {
      existing.name = label;
      return id;
    }
    const row: ActivityGroup = {
      id,
      name: label,
      kind: normalizeTrackerScopeKind(kind),
      groupTypes: [],
    };
    groupById.set(id, row);
    groups.push(row);
    return id;
  };

  const ensureLink = (parentId: string, childId: string) => {
    if (!parentId || !childId || parentId === childId) return;
    const key = linkKey(parentId, childId);
    if (linkSet.has(key)) return;
    linkSet.add(key);
    links.push({ parentId, childId });
  };

  const index = collectHeadScopeIndex(logs);

  const visibleParentId = (scope: ParsedHeadScope): string | null => {
    if (scope.kind === 'location') return orgScope?.id || null;
    if (scope.kind === 'department') {
      const location = walkToHeadKind(index, scope.parentKind, scope.parentRef, 'location')
        || (scope.parentKind === 'location'
          ? findHeadScope(index, 'location', scope.parentRef || '')
          : undefined);
      const locationName = location?.name || (scope.parentKind === 'location' ? scope.parentRef : '');
      return locationName ? ensureVisible('location', locationName) : null;
    }
    if (scope.kind === 'management') {
      const department = walkToHeadKind(index, scope.parentKind, scope.parentRef, 'department');
      return department?.name ? ensureVisible('department', department.name) : null;
    }
    return orgScope?.id || null;
  };

  index.byKindName.forEach((scope) => {
    if (!isLogsHeadScopeKind(scope.kind) || !scope.name) return;
    const id = ensureVisible(scope.kind, scope.name);
    if (!id) return;
    const parentId = visibleParentId(scope);
    if (parentId) ensureLink(parentId, id);
  });

  return { groups, links };
};

export type LogTrackingOverlay = {
  tracking_items?: TrackingItemLike[] | null,
  trackingItems?: TrackingItemLike[] | null,
  /** Master-data scope name (maps from activity_logs.action). */
  action?: string | null,
  type?: string | null,
  groupType?: string | null,
  /** What happened — Record added/change/… (maps from activity_logs.event_type). */
  eventType?: string | null,
  event_type?: string | null,
  department?: string | null,
  departments?: string | null,
  value?: string | number | null,
  units?: string | number | null,
  skus?: Array<{
    id?: string | null,
    name?: string | null,
    value?: string | number | null,
    quantity?: string | number | null,
    unit?: string | null,
    groupTypeId?: string | null,
  }> | null,
  relatedGroups?: Array<{ id?: string | null, kind?: string | null } | string> | null,
  related_groups?: Array<{ id?: string | null, kind?: string | null } | string> | null,
};

const stripNonTransactionLogValue = <T extends LogTrackingOverlay>(log: T): T => {
  const isRecordSaveLog = RECORD_SAVE_EVENT_TYPES.has(logEventTypeRaw(log));
  if (isRecordSaveLog) {
    // Record saves are not POS sales — never surface a unit count.
    return { ...log, value: '', units: null };
  }
  if (isCustomMasterDataTypeCodeValue(log.value)) {
    return { ...log, value: '' };
  }
  return log;
};

/** Overlay legacy columns from trackers — does not mutate the input log. */
export const normalizeLogFromTrackers = <T extends LogTrackingOverlay>(log: T): T => {
  const resolved = resolveLogTracking(log);
  const moneyValue = logMoneyValueForDisplay(log);
  if (!resolved.hasTrackers) return stripNonTransactionLogValue(log);

  const next: T = { ...log };
  // GL name on Action only when this row carries a money value.
  const glOkForAction = Boolean(resolved.glName && moneyValue);
  const masterDataLabel = resolved.objectName || resolved.skuName || resolved.glName || resolved.scopeName;
  // Prefer SKU/object/scope; GL only when this log is a transaction.
  const activityLabel = resolved.objectName
    || resolved.skuName
    || (glOkForAction ? resolved.glName : undefined)
    || resolved.scopeName;

  if (resolved.departmentName) {
    next.departments = resolved.departmentName;
    next.department = resolved.departmentName;
  }

  if (masterDataLabel) {
    next.groupType = masterDataLabel;
  }

  const typeRaw = String(next.type || next.action || '').trim();
  const eventRaw = String(next.eventType || (next as { event_type?: string }).event_type || '').trim();
  const legacyGenericAction = !eventRaw && (
    !typeRaw
    || typeRaw === 'Record added'
    || typeRaw === 'Record updated'
    || typeRaw === 'Record change'
    || typeRaw === 'Record removed'
  );
  const eventIsRecordMutation = eventRaw === 'Record added'
    || eventRaw === 'Record updated'
    || eventRaw === 'Record change'
    || eventRaw === 'Record removed';

  if (activityLabel && (legacyGenericAction || eventIsRecordMutation)) {
    next.type = activityLabel;
    next.action = activityLabel;
  } else if (resolved.glName && !glOkForAction && eventIsRecordMutation) {
    // GL-linked but no transaction — Action column shows the log-type name.
    next.type = eventRaw;
    next.action = eventRaw;
  }

  // Sale qty lives on activity_logs.units from POS writers only — never copy track_uom here.

  if (resolved.skuCode) {
    const gtId = trackerScopeId('sku', resolved.skuName || resolved.skuCode);
    const skuRow = {
      id: resolved.skuCode,
      name: resolved.skuName || resolved.skuCode,
      value: moneyValue,
      quantity: null,
      unit: resolved.uom || null,
      groupTypeId: gtId,
    };
    const prevSkus = [...(next.skus || [])];
    if (!prevSkus.some((s) => String(s?.id || '') === resolved.skuCode)) {
      next.skus = [...prevSkus, skuRow];
    }
  }

  const related = [...(next.relatedGroups || next.related_groups || [])].map((g) => (
    typeof g === 'string' ? { id: g } : g
  ));
  resolved.headScopes.forEach((scope) => {
    const id = trackerScopeId(scope.kind, scope.name || scope.code);
    if (!related.some((g) => String(g?.id || '') === id)) {
      related.push({ id, kind: scope.kind });
    }
  });
  if (related.length) next.relatedGroups = related;

  return stripNonTransactionLogValue(next);
};

export const logHasTrackingItems = (
  log: {
    value?: string | number | null,
    eventType?: string | null,
    event_type?: string | null,
    tracking_items?: TrackingItemLike[] | null,
    trackingItems?: TrackingItemLike[] | null,
  },
): boolean => (
  // Value-column money logs (POS/WO sales) no longer use track_transaction trackers.
  logHasMoneyValue(log) || resolveLogTracking(log).hasTrackers
);

/** Enrich log rows from tracking_items for display — never drops rows. */
export const mapLogsThroughTrackers = <T extends LogTrackingOverlay>(
  logs: T[] | null | undefined,
): T[] => (logs || []).map((log) => normalizeLogFromTrackers(log));
